import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';

import {
	LocalizationContext,
	useLocalize,
	useSupportedLanguageIDs,
	useViewer,
} from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';

import { Flag, FlagProps } from './Flag';
import { Select } from './Select';

export function LanguageSelect(props: {
	value: string;
	onChange: (value: string) => void;
	compact?: boolean;
	small?: boolean;
}) {
	const { t } = useTranslation();
	const { languageIDs } = useSupportedLanguageIDs();

	const languageID = props.value as FlagProps['languageID'];
	const languageTitle = t('common.language.' + languageID);

	return (
		<Select
			small={props.small}
			fit
			tint={false}
			search={false}
			aria-label={languageTitle}
			selectedValue={languageID}
			selectedOption={{
				icon: <Flag languageID={languageID} width={16} />,
				title: props.compact ? '' : languageTitle,
				value: languageID,
			}}
			options={languageIDs.map((languageID: FlagProps['languageID']) => ({
				icon: <Flag languageID={languageID} width={16} />,
				title: t('common.language.' + languageID),
				value: languageID,
			}))}
			onChange={(value) => {
				props.onChange(value!);
			}}
		/>
	);
}

function LanguageSelectScope() {
	const context = useContext(LocalizationContext);

	return <LanguageSelect value={context.languageID} onChange={context.setLanguageID} />;
}

LanguageSelect.Synced = function SyncedLanguageSelect(props: {
	compact?: boolean;
	small?: boolean;
}) {
	const { language } = useLocalize();

	if (language.loading) {
		return null;
	}

	return (
		<SyncedLanguageSelectInner language={language} compact={props.compact} small={props.small} />
	);
};

function SyncedLanguageSelectInner({
	language,
	compact,
	small,
}: {
	language: { id: string };
	compact?: boolean;
	small?: boolean;
}) {
	const [, setCurrentLanguage] = useLocalStorage('locale', language.id);
	const viewer = useViewer();

	const [updateUserLocale] = graphql.useUpdateUserLocaleMutation();

	const update = async (languageId: string) => {
		if (viewer) {
			// Once the locale has been updated, useLanguage hook will automatically detect the
			// change and update local storage.
			//
			// useWorkspaceLocale will automatically detect the change and update i18n and moment.
			await updateUserLocale({
				variables: {
					input: {
						userID: viewer.id,
						locale: languageId,
					},
				},
			});
		} else {
			setCurrentLanguage(languageId);
		}
	};

	const onChange = useCallback((language: string) => {
		update(language);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return <LanguageSelect value={language.id} onChange={onChange} compact={compact} small={small} />;
}

LanguageSelect.Scope = LanguageSelectScope;
